import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Pagination, Stack } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import BrandCard from "../components/brands/BrandCard";
import CreateBrandModal from "../components/brands/CreateBrandModal";
import UpdateBrandModal from "../components/brands/UpdateBrandModal";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getBrands,
  deleteBrand,
  resetBrandServerStatus,
} from "../redux/actions/brandActions";
import { BrandResponseDto } from "../shared/dtos/brand/BrandResponseDto";

const BrandsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: brands,
    pagination,
    loadingFetchAll: loadingBrands,
    errorFetchAll: errorLoadingBrands,
    serverStatus,
    // loadingDelete: loadingBrandDelete,
    loadingUpdate: loadingBrandUpdate,
    loadingCreate: loadingBrandCreate,
    errorCreate: errorCreatingBrand,
    errorUpdate: errorUpdatingBrand,
  } = useAppSelector((state) => state.brand);

  const [page, setPage] = useState<number>(1);
  const limit = 12;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedBrand, setSelectedBrand] = useState<BrandResponseDto | null>(
    null,
  );

  const canCreate = hasPermission("brand:create");
  const canUpdate = hasPermission("brand:update");
  const canDelete = hasPermission("brand:delete");

  const fetchBrandsData = useCallback(() => {
    dispatch(getBrands({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchBrandsData();
  }, [fetchBrandsData]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;
    if (operation === "create" && (status === 201 || status === 200))
      setIsCreateModalOpen(false);
    if (operation === "update" && status === 200) setIsUpdateModalOpen(false);
    if (operation === "delete" && status === 200) setIsDeleteModalOpen(false);
    dispatch(resetBrandServerStatus());
  }, [serverStatus, dispatch]);

  const renderContent = (): React.JSX.Element => {
    if (loadingBrands) return <AppCircularProgress />;
    if (errorLoadingBrands)
      return <PageError error={errorLoadingBrands} onRetry={fetchBrandsData} />;

    if (!brands || brands.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography>No brands found.</Typography>
        </Box>
      );

    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          {brands.map((brand) => (
            <BrandCard
              key={brand.id}
              brand={brand as unknown as BrandResponseDto}
              onEdit={
                canUpdate
                  ? () => {
                      setSelectedBrand(brand as unknown as BrandResponseDto);
                      setIsUpdateModalOpen(true);
                    }
                  : undefined
              }
              onDelete={
                canDelete
                  ? () => {
                      setSelectedBrand(brand as unknown as BrandResponseDto);
                      setIsDeleteModalOpen(true);
                    }
                  : undefined
              }
            />
          ))}
        </Box>

        {pagination?.totalPages && pagination.totalPages > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Product Brands"
      subtitle="Manage product manufacturers and brand identities."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Add Brand
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search brands..."
      />
      {renderContent()}

      {isCreateModalOpen && (
        <CreateBrandModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          loading={loadingBrandCreate}
          error={errorCreatingBrand}
        />
      )}
      {isUpdateModalOpen && selectedBrand && (
        <UpdateBrandModal
          open={isUpdateModalOpen}
          onClose={() => setIsUpdateModalOpen(false)}
          brand={selectedBrand}
          loading={loadingBrandUpdate}
          error={errorUpdatingBrand}
        />
      )}
      {isDeleteModalOpen && selectedBrand && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={() => dispatch(deleteBrand(selectedBrand.id))}
          title="Delete Brand"
          description={`Delete "${selectedBrand.name}"?`}
          //loading={loadingBrandDelete}
        />
      )}
    </PageContainer>
  );
};

export default BrandsPage;
